/**
 * Skeleton Loading Components
 * Animated placeholders for loading states
 */

import { cn } from '@/lib/utils'

interface SkeletonProps {
  className?: string
}

// Base skeleton with shimmer animation
export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg bg-elevated',
        'before:absolute before:inset-0',
        'before:translate-x-[-100%]',
        'before:animate-[shimmer_1.5s_infinite]',
        'before:bg-gradient-to-r',
        'before:from-transparent before:via-white/5 before:to-transparent',
        className
      )}
    />
  )
}

// Text line skeleton
export function SkeletonText({ className, lines = 1 }: SkeletonProps & { lines?: number }) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-4',
            i === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'
          )}
        />
      ))}
    </div>
  )
}

// Circle skeleton (for avatars)
export function SkeletonCircle({ className, size = 'md' }: SkeletonProps & { size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  }

  return <Skeleton className={cn('rounded-full', sizeClasses[size], className)} />
}

// Card skeleton
export function SkeletonCard({ className }: SkeletonProps) {
  return (
    <div className={cn('card space-y-3', className)}>
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-4 w-16" />
      </div>
      <Skeleton className="h-12 w-full" />
      <div className="flex gap-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-24" />
      </div>
    </div>
  )
}

// Chart skeleton
export function SkeletonChart({ className, height = 'h-32' }: SkeletonProps & { height?: string }) {
  return (
    <div className={cn('card', className)}>
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-6 w-24 rounded-full" />
      </div>
      <div className={cn('relative', height)}>
        <Skeleton className="absolute inset-0 rounded-lg" />
        {/* Fake bars for visual interest */}
        <div className="absolute bottom-0 left-0 right-0 flex items-end justify-around px-4 pb-2 gap-2">
          {[40, 65, 45, 80, 55, 70, 50].map((h, i) => (
            <div
              key={i}
              className="w-full bg-elevated/50 rounded-t"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

// Stats row skeleton
export function SkeletonStats({ className, count = 3 }: SkeletonProps & { count?: number }) {
  return (
    <div className={cn('grid gap-3', className)} style={{ gridTemplateColumns: `repeat(${count}, 1fr)` }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="text-center space-y-2">
          <Skeleton className="h-7 w-16 mx-auto" />
          <Skeleton className="h-3 w-20 mx-auto" />
        </div>
      ))}
    </div>
  )
}

// List item skeleton
export function SkeletonListItem({ className }: SkeletonProps) {
  return (
    <div className={cn('flex items-center gap-3 py-3', className)}>
      <SkeletonCircle size="md" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <Skeleton className="h-4 w-12" />
    </div>
  )
}

// Dashboard bento cell skeleton
export function SkeletonBentoCell({ className, size = 'normal' }: SkeletonProps & { size?: 'small' | 'normal' | 'large' }) {
  const heightClasses = {
    small: 'h-24',
    normal: 'h-36',
    large: 'h-48',
  }

  return (
    <div className={cn('card', heightClasses[size], className)}>
      <div className="flex items-center justify-between mb-3">
        <Skeleton className="h-4 w-24" />
        <SkeletonCircle size="sm" />
      </div>
      <Skeleton className="h-8 w-20 mb-2" />
      <Skeleton className="h-3 w-full" />
    </div>
  )
}
